import {
    ArrowPathIcon,
    CheckCircleIcon,
    ClockIcon,
    XMarkIcon,
} from "@heroicons/react/20/solid";
import { ago } from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import React from "react";

interface SyncResult {
    status: string;
    queuedAt: string;
    startedAt?: string | null;
    completedAt?: string | null;
    errorMessage?: string | null;
    membersAdded?: number | null;
    membersUpdated?: number | null;
    membersRemoved?: number | null;
}

interface AudienceSyncStatusProps {
    syncResult: SyncResult | null | undefined;
    providerDisplayName: string;
}

export const AudienceSyncStatus: React.FC<AudienceSyncStatusProps> = ({
    syncResult,
    providerDisplayName,
}) => {
    if (!syncResult) {
        return null;
    }

    return (
        <div className="space-y-1">
            <p className="text-sm">Synchronization Status</p>
            <div className="flex items-center space-x-2">
                {renderStatusBody(syncResult, providerDisplayName)}
            </div>
        </div>
    );
};

function renderStatusBody(
    syncResult: SyncResult,
    providerDisplayName: string,
): React.ReactNode {
    if (syncResult.status === "Queued") {
        return (
            <>
                <ClockIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                    Sync queued — typically starts within a minute and runs in the
                    background. You can leave this page.
                </span>
            </>
        );
    }
    if (syncResult.status === "Running") {
        const since = syncResult.startedAt ?? syncResult.queuedAt;
        return (
            <>
                <ArrowPathIcon className="h-4 w-4 text-gray-500 animate-spin" />
                <span className="text-sm text-gray-600">
                    Syncing now (started {ago(since)}) — most syncs finish in a minute or
                    two; larger audiences may take longer. You can leave this page.
                </span>
            </>
        );
    }
    if (syncResult.status === "Success" || syncResult.status === "PartialSuccess") {
        const at = syncResult.completedAt ?? syncResult.queuedAt;
        return (
            <>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <span className="text-sm text-gray-600">
                    Last synchronized {ago(at)}
                    {syncResult.status === "PartialSuccess" ? " (partial)" : ""}
                </span>
            </>
        );
    }
    const failedAt = syncResult.completedAt ?? syncResult.queuedAt;
    return (
        <>
            <XMarkIcon className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-600">
                Synchronization failed {ago(failedAt)}.{" "}
                {syncResult.errorMessage ? `${syncResult.errorMessage} ` : ""}
                Please{" "}
                <SupportEmailLink
                    label="contact support"
                    subject={`${providerDisplayName} Synchronization Error`}
                />
                .
            </span>
        </>
    );
}

export const isInFlight = (syncResult: SyncResult | null | undefined): boolean => {
    if (!syncResult) return false;
    return syncResult.status === "Queued" || syncResult.status === "Running";
};
